import { type ReactNode, forwardRef, useMemo } from 'react';
import { Platform } from 'react-native';
import {
  Modal,
  type ModalProps,
  type StyleProp,
  StyleSheet,
  View,
  type ViewStyle,
} from 'react-native';

import {
  IconClose,
  Pressable,
  Text,
  XStack,
  YStack,
  createMqStyles,
  tokens,
  useMqSelect,
} from '@fhs/ui';

import { useFocusWebAriaModalOnOpen } from '../action-sheet/use-focus-web-aria-modal-on-open';
import { useSuppressWebRootAccessibility } from '../action-sheet/use-suppress-web-root-accessibility';

export type AlertDialogProps = Omit<
  ModalProps,
  'presentationStyle' | 'transparent' | 'visible' | 'style'
> & {
  // Always use `visible` prop to determine visibility. Do not conditionally mount.
  // Set visible to false instead of unmounting the dialog.
  // e.g.,
  // ```tsx
  // const [modalIsVisible, setModalIsVisible] = useState(false);
  // return <AlertDialog visible={modalIsVisible} .../>
  // ```
  // This prevents flickering issues that can happen otherwise.
  visible: boolean;
  onClose: () => void;
  hideCloseIcon?: boolean;
  backdropStyle?: StyleProp<ViewStyle>;
  headingContent?: ReactNode;
  bodyContent?: ReactNode;
  actions?: ReactNode;
  style?: StyleProp<ViewStyle>;
  accessibilityLabel?: string;
};

export const AlertDialog = forwardRef<Modal, AlertDialogProps>(
  (
    {
      backdropStyle,
      headingContent,
      bodyContent,
      actions,
      onClose,
      style,
      testID,
      hideCloseIcon,
      accessibilityLabel,
      visible,
      ...modalProps
    },
    forwardedRef
  ) => {
    const mqStyles = useMqStyles();
    const isDesktop = useMqSelect({ $gteDesktop: true }, false);
    const dialogLabel = useMemo(
      () => (accessibilityLabel?.trim() ? accessibilityLabel.trim() : 'Dialog'),
      [accessibilityLabel]
    );
    const closeLabel = useMemo(() => `Close ${dialogLabel}`, [dialogLabel]);

    const isWeb = Platform.OS === 'web';
    useSuppressWebRootAccessibility(isWeb && Boolean(visible));
    useFocusWebAriaModalOnOpen(isWeb && Boolean(visible));

    return (
      <Modal
        ref={forwardedRef}
        transparent
        animationType="fade"
        visible={visible}
        {...modalProps}
        {...(isWeb
          ? ({
              // RN Web ModalContent applies role="dialog" + aria-modal on this node only; name must
              // live here—not on inner content—or Lighthouse/SRs see an unnamed dialog.
              accessibilityLabel: dialogLabel,
              'data-fhs-action-sheet-dialog': 'true',
            } as Record<string, unknown>)
          : {})}
      >
        <View style={[mqStyles.modalBackdrop, backdropStyle]}>
          <View style={mqStyles.backdropPositioner}>
            <YStack
              accessibilityLabel={isWeb ? undefined : dialogLabel}
              accessibilityViewIsModal={!isWeb}
              accessibilityRole={isWeb ? 'none' : 'alert'}
              style={[mqStyles.modalContent, style]}
              testID={testID}
            >
              <YStack style={[mqStyles.textContentArea]}>
                {headingContent && (
                  <View style={mqStyles.headingContentArea}>
                    {typeof headingContent === 'string' ? (
                      <Text.Heading type="two" accessibilityRole="header">
                        {headingContent}
                      </Text.Heading>
                    ) : (
                      headingContent
                    )}
                  </View>
                )}

                {bodyContent &&
                  (typeof bodyContent === 'string' ? (
                    <Text.Paragraph size="md">{bodyContent}</Text.Paragraph>
                  ) : (
                    bodyContent
                  ))}

                {!hideCloseIcon && (
                  <View style={mqStyles.closeIconWrapper}>
                    <Pressable
                      accessibilityLabel={closeLabel}
                      accessibilityRole="button"
                      onPress={onClose}
                      {...(isWeb
                        ? ({
                            'aria-label': closeLabel,
                            'data-fhs-action-sheet-dismiss': 'true',
                          } as Record<string, unknown>)
                        : {})}
                    >
                      <IconClose
                        size={isDesktop ? 32 : 24}
                        accessibilityElementsHidden
                        importantForAccessibility="no-hide-descendants"
                      />
                    </Pressable>
                  </View>
                )}
              </YStack>
              {actions &&
                (isDesktop ? (
                  <View>
                    <XStack style={mqStyles.actionArea}>{actions}</XStack>
                  </View>
                ) : (
                  <YStack style={mqStyles.actionArea}>{actions}</YStack>
                ))}
            </YStack>
          </View>
        </View>
      </Modal>
    );
  }
);

const useMqStyles = createMqStyles({
  modalBackdrop: {
    $base: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: tokens.colors.$blackOpacity75,
      overflow: 'scroll',
    },
  },
  backdropPositioner: {
    $base: {
      minWidth: '100%',
      minHeight: '100%',
      justifyContent: 'center',
      alignItems: 'center',
    },
  },
  modalContent: {
    $base: {
      borderRadius: 20,
      backgroundColor: tokens.colors.$white,
      width: '100%',
      margin: 24,
      maxWidth: 350,
      padding: 24,
      gap: 20,
    },
    $gteDesktop: {
      maxWidth: 520,
      padding: 28,
      gap: 28,
    },
  },
  textContentArea: {
    $base: {
      gap: 12,
    },
    $gteDesktop: {
      gap: 28,
    },
  },
  headingContentArea: {
    $base: {
      paddingRight: 34, // space for close button
    },
  },
  actionArea: {
    $base: {
      gap: 8,
    },
    $gteDesktop: {
      flexDirection: 'row-reverse',
    },
  },
  closeIconWrapper: {
    $base: {
      alignSelf: 'flex-end',
      marginBottom: 8,
      position: 'absolute',
    },
  },
});
